import React, { useState, useRef, useMemo, useEffect } from 'react';
import { toast } from 'sonner';
import { UploadCloud, CheckCircle2, XCircle, Loader2, ArrowRight, ArrowLeft, RotateCcw, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { groupFilesByProduct } from '../lib/imageFilename';

const API = process.env.REACT_APP_BACKEND_URL;
const BUCKET = 'product-images';

const STEP_BLUE = '#0ea5e9';
const STEP_DONE = '#10b981';

const Step = ({ n, label, active, done }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    color: done ? STEP_DONE : active ? STEP_BLUE : '#94a3b8',
    fontWeight: active || done ? 700 : 500,
    fontSize: 14,
  }}>
    <span style={{
      width: 30, height: 30, borderRadius: '50%',
      background: done ? STEP_DONE : active ? STEP_BLUE : '#e2e8f0',
      color: done || active ? '#fff' : '#64748b',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 13, fontWeight: 700,
      boxShadow: active || done ? '0 2px 8px rgba(14, 165, 233, 0.25)' : 'none',
    }}>{done ? '✓' : n}</span>
    <span>{label}</span>
  </div>
);

export default function ImageUploadTab() {
  const [step, setStep] = useState(1);            // 1=parse, 2=verify, 3=publish
  const [groups, setGroups] = useState({});        // { partNo: { main, additional[] } }
  const [unknownFiles, setUnknownFiles] = useState([]);
  const [matchedMappings, setMatchedMappings] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [verifications, setVerifications] = useState({}); // partNo → lookup-resultat
  const [verifying, setVerifying] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishResults, setPublishResults] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const fileInputRef = useRef(null);

  // Hämta aktiva mappningar en gång
  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const r = await fetch(`${API}/api/images/mappings`, {
          headers: { 'Authorization': `Bearer ${session?.access_token || ''}` },
        });
        if (r.ok) {
          const data = await r.json();
          setMappings(data.mappings || []);
        }
      } catch { /* tyst — mappningar är optional */ }
    })();
  }, []);

  const partNos = useMemo(() => Object.keys(groups), [groups]);

  const handleFiles = (filesList) => {
    const files = Array.from(filesList).filter(f =>
      /\.(jpe?g|png|webp|gif|avif)$/i.test(f.name)
    );
    if (files.length === 0) {
      toast.error('Inga giltiga bildfiler hittades.');
      return;
    }
    const { groups: newGroups, unknown, matchedMappingIds } = groupFilesByProduct(files, mappings);
    setGroups(prev => {
      const merged = { ...prev };
      Object.entries(newGroups).forEach(([pn, g]) => {
        if (!merged[pn]) merged[pn] = { main: null, additional: [] };
        if (g.main && !merged[pn].main) merged[pn].main = g.main;
        merged[pn].additional = [...merged[pn].additional, ...g.additional];
      });
      return merged;
    });
    setUnknownFiles(prev => [...prev, ...unknown]);
    setMatchedMappings(prev => Array.from(new Set([...prev, ...matchedMappingIds])));
    const okCount = files.length - unknown.length;
    toast.success(
      `${okCount} fil(er) parsade till ${Object.keys(newGroups).length} produkt(er).` +
      (unknown.length ? ` ${unknown.length} okänd.` : '')
    );
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const removePart = (partNo) => {
    setGroups(prev => {
      const next = { ...prev };
      delete next[partNo];
      return next;
    });
  };

  const reset = () => {
    setStep(1);
    setGroups({});
    setUnknownFiles([]);
    setMatchedMappings([]);
    setVerifications({});
    setPublishResults([]);
    setProgress({ done: 0, total: 0 });
  };

  // Bumpa match_count för använda mappningar (best-effort)
  const bumpMatchedMappings = async () => {
    if (matchedMappings.length === 0) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await fetch(`${API}/api/images/mappings/bump`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify({ mapping_ids: matchedMappings }),
      });
    } catch { /* tyst */ }
  };

  // Skapa thumbnail-URL per File (frigörs vid reset/unmount)
  const fileUrls = useMemo(() => {
    const map = new WeakMap();
    Object.values(groups).forEach(g => {
      if (g.main && !map.has(g.main)) map.set(g.main, URL.createObjectURL(g.main));
      g.additional.forEach(f => { if (!map.has(f)) map.set(f, URL.createObjectURL(f)); });
    });
    return map;
  }, [groups]);

  // ─── Step 2: verify against Norce ─────────────────────────────────────────
  const goVerify = async () => {
    if (partNos.length === 0) {
      toast.error('Lägg till bildfiler först.');
      return;
    }
    setVerifying(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`${API}/api/images/lookup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify({ part_nos: partNos }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
      const data = await r.json();
      const map = {};
      (data.results || []).forEach(row => { map[row.part_no] = row; });
      setVerifications(map);
      setStep(2);
    } catch (e) {
      toast.error(`Verifiering misslyckades: ${e.message}`);
    } finally {
      setVerifying(false);
    }
  };

  // ─── Step 3: upload to Supabase Storage + publish to Norce ────────────────
  const uploadToStorage = async (file, partNo, kind, idx) => {
    const ext = file.name.split('.').pop().toLowerCase();
    const path = `${partNo}/${kind}-${idx}-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
      cacheControl: '3600', upsert: true, contentType: file.type,
    });
    if (error) throw new Error(`Storage: ${error.message}`);
    const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
    return data.publicUrl;
  };

  const goPublish = async () => {
    const verifiedParts = partNos.filter(pn => verifications[pn]?.status === 'verified');
    if (verifiedParts.length === 0) {
      toast.error('Inga verifierade produkter att publicera.');
      return;
    }
    setPublishing(true);
    setStep(3);
    await bumpMatchedMappings();
    setProgress({ done: 0, total: verifiedParts.length });
    const results = [];

    for (let i = 0; i < verifiedParts.length; i++) {
      const partNo = verifiedParts[i];
      const g = groups[partNo];
      try {
        // 1. Ladda upp filer till Supabase Storage
        let mainUrl = null;
        if (g.main) mainUrl = await uploadToStorage(g.main, partNo, 'main', 1);
        const addUrls = [];
        for (let j = 0; j < g.additional.length; j++) {
          const u = await uploadToStorage(g.additional[j], partNo, 'extra', j + 2);
          addUrls.push(u);
        }

        // 2. Skicka till Norce via backend
        const { data: { session } } = await supabase.auth.getSession();
        const r = await fetch(`${API}/api/images/publish`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token || ''}`,
          },
          body: JSON.stringify({
            products: [{
              part_no: partNo,
              main_image_url: mainUrl,
              additional_image_urls: addUrls,
            }],
          }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}: ${(await r.text()).slice(0, 200)}`);
        const data = await r.json();
        const res = (data.results || [])[0] || { success: false, error: 'No response' };
        results.push({ part_no: partNo, ...res });
      } catch (e) {
        results.push({ part_no: partNo, success: false, error: e.message });
      }
      setProgress({ done: i + 1, total: verifiedParts.length });
      setPublishResults([...results]);
    }
    setPublishing(false);
    const ok = results.filter(r => r.success).length;
    toast.success(`${ok}/${results.length} produkter publicerade.`);
  };

  return (
    <div data-testid="image-upload-tab">
      {/* Stegindikator */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 18,
        padding: '16px 22px', background: '#fff', border: '1px solid #e2e8f0',
        borderRadius: 12, marginBottom: 28,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <Step n={1} label="Ladda upp & parsa" active={step === 1} done={step > 1} />
        <ArrowRight size={14} color="#cbd5e1" />
        <Step n={2} label="Verifiera mot Norce" active={step === 2} done={step > 2} />
        <ArrowRight size={14} color="#cbd5e1" />
        <Step n={3} label="Publicera" active={step === 3} done={false} />
      </div>

      {/* ─── STEG 1: parse ─── */}
      {step === 1 && (
        <div>
          <div
            data-testid="dropzone"
            onDragOver={e => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: '2px dashed #cbd5e1', borderRadius: 14,
              padding: 56, textAlign: 'center', cursor: 'pointer',
              background: '#f8fafc', transition: 'all 0.2s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = '#eff6ff';
              e.currentTarget.style.borderColor = STEP_BLUE;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = '#f8fafc';
              e.currentTarget.style.borderColor = '#cbd5e1';
            }}
          >
            <UploadCloud size={42} style={{ color: STEP_BLUE, margin: '0 auto 12px' }} />
            <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 16, color: '#0f172a' }}>
              Dra & släpp bildfiler — eller klicka för att välja
            </div>
            <div style={{ fontSize: 13, color: '#64748b' }}>
              Filnamn: <code>PARTNO.jpg</code> = huvudbild,{' '}
              <code>PARTNO_2.jpg</code>, <code>PARTNO-3.jpg</code>, <code>PARTNO (2).jpg</code> = extrabilder
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              style={{ display: 'none' }}
              onChange={e => handleFiles(e.target.files)}
              data-testid="file-input"
            />
          </div>

          {(partNos.length > 0 || unknownFiles.length > 0) && (
            <>
              <div style={{
                marginTop: 24, display: 'flex', alignItems: 'center',
                gap: 12, flexWrap: 'wrap',
              }}>
                <span style={{ fontWeight: 600, fontSize: 14, color: '#374151' }}>
                  {partNos.length} produkt(er) parsade
                </span>
                {matchedMappings.length > 0 && (
                  <span style={{
                    background: '#dbeafe', color: '#1e40af', padding: '4px 10px',
                    borderRadius: 12, fontSize: 12, fontWeight: 600,
                  }} data-testid="mappings-used-badge">
                    {matchedMappings.length} mappning(ar) använda
                  </span>
                )}
                {unknownFiles.length > 0 && (
                  <span style={{
                    background: '#fed7aa', color: '#9a3412', padding: '4px 10px',
                    borderRadius: 12, fontSize: 12, fontWeight: 600,
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                  }} data-testid="unknown-files-badge">
                    <AlertCircle size={12} /> {unknownFiles.length} okänd(a)
                  </span>
                )}
              </div>
              {partNos.length > 0 && (
                <div
                  data-testid="image-cards-grid"
                  style={{
                    marginTop: 18,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: 16,
                  }}
                >
                  {partNos.flatMap(pn => {
                    const g = groups[pn];
                    const cards = [];
                    if (g.main) {
                      cards.push({ file: g.main, pn, kind: 'main', label: 'Main', idx: 1 });
                    }
                    g.additional.forEach((f, i) => {
                      cards.push({ file: f, pn, kind: 'additional', label: `Additional (${i + 2})`, idx: i + 2 });
                    });
                    return cards;
                  }).map(({ file, pn, kind, label, idx }) => (
                    <div
                      key={`${pn}-${kind}-${idx}-${file.name}`}
                      data-testid={`image-card-${pn}-${kind}-${idx}`}
                      style={{
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 12,
                        overflow: 'hidden',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
                        transition: 'transform 0.15s, box-shadow 0.15s',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.06), 0 12px 24px rgba(0,0,0,0.08)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)';
                      }}
                    >
                      <div style={{
                        width: '100%', aspectRatio: '1 / 1', background: '#f3f4f6',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        overflow: 'hidden',
                      }}>
                        <img
                          src={fileUrls.get(file)}
                          alt={file.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                      <div style={{ padding: '12px 14px' }}>
                        <div style={{
                          fontSize: 12, fontWeight: 600, color: '#111827',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          marginBottom: 4,
                        }} title={file.name}>
                          {file.name}
                        </div>
                        <div style={{
                          fontSize: 11, color: '#6b7280', fontFamily: 'monospace',
                          marginBottom: 8,
                        }}>{pn}</div>
                        <span style={{
                          display: 'inline-block',
                          padding: '3px 10px', borderRadius: 12,
                          fontSize: 11, fontWeight: 600,
                          background: kind === 'main' ? '#dbeafe' : '#e5e7eb',
                          color:      kind === 'main' ? '#1e40af' : '#374151',
                        }}>{label}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {partNos.length > 0 && (
                <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="btn-text" onClick={reset} data-testid="btn-clear-all">
                    Rensa alla
                  </button>
                </div>
              )}

              {unknownFiles.length > 0 && (
                <div data-testid="unknown-section" style={{
                  marginTop: 16, padding: 14, background: '#fff7ed',
                  border: '1px solid #fed7aa', borderRadius: 10,
                }}>
                  <div style={{ fontWeight: 600, color: '#9a3412', marginBottom: 8, fontSize: 13 }}>
                    Okända filer — varken standardparser eller mappningar matchade
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: '#7c2d12' }}>
                    {unknownFiles.map((f, i) => (
                      <li key={i}><code>{f.name}</code></li>
                    ))}
                  </ul>
                  <p style={{ marginTop: 8, fontSize: 12, color: '#7c2d12' }}>
                    Lägg till en mappningsregel i fliken <strong>Mappningar</strong> och försök igen.
                    Dessa filer publiceras <strong>inte</strong>.
                  </p>
                </div>
              )}
              {partNos.length > 0 && (
                <div style={{ marginTop: 18 }}>
                  <button
                    className={`generate-btn${!verifying ? ' generate-btn--active' : ''}`}
                    disabled={verifying}
                    onClick={goVerify}
                    data-testid="btn-verify"
                  >
                    {verifying
                      ? <><span className="spinner" /> Verifierar...</>
                      : <><ArrowRight size={15} /> Nästa: Verifiera mot Norce</>}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ─── STEG 2: verify ─── */}
      {step === 2 && (
        <div>
          <table className="data-table" data-testid="verify-table">
            <thead>
              <tr>
                <th>PartNo</th>
                <th>Produktnamn</th>
                <th>Tillverkare</th>
                <th>Status</th>
                <th>Bilder</th>
              </tr>
            </thead>
            <tbody>
              {partNos.map(pn => {
                const v = verifications[pn];
                const ok = v?.status === 'verified';
                return (
                  <tr key={pn} data-testid={`verify-row-${pn}`}>
                    <td><code>{pn}</code></td>
                    <td>{v?.product_name || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    <td>{v?.manufacturer_code || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    <td>
                      {ok ? (
                        <span style={{ color: '#10b981', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <CheckCircle2 size={14} /> Hittad
                        </span>
                      ) : (
                        <span style={{ color: '#ef4444', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <XCircle size={14} /> {v?.error || 'Ej hittad'}
                        </span>
                      )}
                    </td>
                    <td>
                      {(groups[pn].main ? 1 : 0) + groups[pn].additional.length} st
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
            <button className="btn-text" onClick={() => setStep(1)} data-testid="btn-back-1">
              <ArrowLeft size={14} /> Tillbaka
            </button>
            <button
              className="generate-btn generate-btn--active"
              onClick={goPublish}
              data-testid="btn-publish"
              style={{ width: 'auto', minWidth: 280 }}
            >
              <ArrowRight size={15} /> Publicera till Norce
            </button>
          </div>
        </div>
      )}

      {/* ─── STEG 3: publish ─── */}
      {step === 3 && (
        <div>
          <div style={{ marginBottom: 16, fontWeight: 600 }}>
            {publishing
              ? `Publicerar ${progress.done}/${progress.total} produkter...`
              : `Klart — ${publishResults.filter(r => r.success).length}/${publishResults.length} lyckades`}
          </div>
          <div style={{
            height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden', marginBottom: 20,
          }}>
            <div style={{
              height: '100%',
              width: progress.total ? `${(progress.done / progress.total) * 100}%` : '0%',
              background: 'var(--accent)', transition: 'width 0.3s',
            }} />
          </div>

          <table className="data-table" data-testid="publish-table">
            <thead>
              <tr>
                <th>PartNo</th>
                <th>Status</th>
                <th>Bilder</th>
                <th>JobId / Fel</th>
              </tr>
            </thead>
            <tbody>
              {publishResults.map(r => (
                <tr key={r.part_no} data-testid={`publish-row-${r.part_no}`}>
                  <td><code>{r.part_no}</code></td>
                  <td>
                    {r.success ? (
                      <span style={{ color: '#10b981' }}>✓ Publicerad</span>
                    ) : (
                      <span style={{ color: '#ef4444' }}>✗ Misslyckades</span>
                    )}
                  </td>
                  <td>{r.image_count ?? '-'}</td>
                  <td>
                    {r.success
                      ? <code style={{ fontSize: 11, color: '#6b7280' }}>{r.job_id}</code>
                      : <span style={{ color: '#ef4444', fontSize: 12 }}>{r.error}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!publishing && (
            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className="generate-btn generate-btn--active"
                onClick={reset}
                data-testid="btn-reset"
                style={{ width: 'auto', minWidth: 220 }}
              >
                <RotateCcw size={15} /> Ny uppladdning
              </button>
            </div>
          )}
        </div>
      )}

      {/* ─── Sticky footer borttagen — primärknapp finns inline ovanför ─── */}
    </div>
  );
}
